/**
 * Aritmetik doğrulama — belgenin kendi rakamlarıyla tutarlılık.
 *
 * İki işe birden yarar ve ikisi de değerlidir:
 *
 *   1. BİZİM HATAMIZ — belgede "Toplam: 5.400" yazıyor ama okuduğumuz
 *      kalemler 4.900 ediyorsa bir kalem kaçmıştır. Kullanıcıyı eksik bir
 *      defteri onaylamaktan kurtarır. Kaçan satır, yanlış okunan satırdan
 *      daha tehlikelidir: görünmez.
 *
 *   2. MÜŞTERİNİN HATASI — faturanın kendi toplamı kalemleriyle tutmuyorsa
 *      bu bizim değil belgenin hatasıdır. Muhasebeciye para kazandıran bilgi
 *      budur; "sistem yanlış okumadı, faturanız yanlış" demek güven kurar.
 *
 * Vergi oranı LİSTESİ YOKTUR. Hangi oranın geçerli olduğu ülkeye ve yıla göre
 * değişir ve sabit bir liste hızla yanlışa döner. Yaptığımız kontrol saf
 * aritmetiktir: net + vergi = brüt mü? Bu her ülkede, her yıl doğrudur.
 */

/** Kuruş yuvarlamasına tolerans. Altındaki fark hata sayılmaz. */
export const EPSILON = 0.02;

export interface TotalsCheck {
  /** Aritmetik tutuyor mu. */
  readonly ok: boolean;
  /** Beklenen ile bulunan arasındaki fark (bulunan − beklenen). */
  readonly difference: number;
  /** Kullanıcıya gösterilecek açıklama. `ok` ise boş dize. */
  readonly reason: string;
}

const OK: TotalsCheck = { ok: true, difference: 0, reason: '' };

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function fmt(n: number): string {
  return round2(Math.abs(n)).toFixed(2);
}

/**
 * net + vergi = brüt mü?
 *
 * Üç değerden herhangi biri yoksa kontrol YAPILMAZ ve `ok` döner: eksik veriyi
 * hata gibi göstermek, gerçek hataların arasında kaybolmasına yol açar.
 */
export function checkVatArithmetic(
  net: number | null,
  tax: number | null,
  gross: number | null,
): TotalsCheck {
  if (net === null || tax === null || gross === null) return OK;
  if (![net, tax, gross].every((n) => Number.isFinite(n))) return OK;

  const diff = round2(net + tax - gross);
  if (Math.abs(diff) <= EPSILON) return OK;

  return {
    ok: false,
    difference: diff,
    reason:
      `The document's own total does not add up: ${fmt(net)} + ${fmt(tax)} = `
      + `${fmt(net + tax)}, but the document says ${fmt(gross)} `
      + `(${diff > 0 ? 'over' : 'short'} by ${fmt(diff)}).`,
  };
}

/**
 * Okunan kalemlerin toplamı, belgenin ilan ettiği toplamla tutuyor mu?
 *
 * Bu, KAÇAN SATIRI yakalayan kontroldür. Fark pozitifse fazla okumuşuz,
 * negatifse eksik okumuşuz demektir.
 */
export function reconcileTotal(
  amounts: readonly number[],
  declaredTotal: number | null,
): TotalsCheck {
  if (declaredTotal === null || !Number.isFinite(declaredTotal)) return OK;

  const sum = round2(
    amounts.filter((n) => Number.isFinite(n)).reduce((a, b) => a + Math.abs(b), 0),
  );
  const diff = round2(sum - Math.abs(declaredTotal));
  if (Math.abs(diff) <= EPSILON) return OK;

  const eksik = diff < 0;
  return {
    ok: false,
    difference: diff,
    reason:
      `The document states a total of ${fmt(declaredTotal)} but the line items `
      + `add up to ${fmt(sum)}. ${eksik
        ? 'A line item may have been missed - please check.'
        : 'An extra line may have been read - please check.'}`,
  };
}

/**
 * Metinde ilan edilmiş toplamı ara.
 *
 * Yalnızca "toplam" anlamına gelen etiketlerin ardındaki sayıyı kabul eder;
 * belgedeki en büyük sayıyı toplam saymak gibi bir tahmin YAPMAZ. Bulamazsa
 * null döner ve mutabakat kontrolü hiç çalışmaz — yanlış bir "toplam"la
 * sahte uyarı üretmek, hiç uyarmamaktan kötüdür.
 *
 * @param parseValue Tutar okuyucusu. Yerel ayara duyarlı olan dışarıdan
 *   verilir; bu dosya para biçimlerini bilmek zorunda değildir.
 */
export function findDeclaredTotal(
  lines: readonly string[],
  parseValue: (raw: string) => number | null,
): number | null {
  /* "ara toplam" ve "vergi matrahı" bilerek DIŞARIDA: onlar genel toplam
     değildir ve kalemlerle karşılaştırılırsa sahte uyarı üretirler. */
  const LABEL =
    /(genel\s*toplam|toplam\s*tutar|ödenecek|odenecek|grand\s*total|total\s*due|amount\s*due|invoice\s*total|gesamtbetrag|gesamtsumme|montant\s*total|importe\s*total|totale)/i;
  const EXCLUDE = /(ara\s*toplam|subtotal|zwischensumme|matrah|net\s*total|sous-total)/i;

  const found: number[] = [];
  for (const raw of lines) {
    const line = String(raw ?? '');
    if (!LABEL.test(line) || EXCLUDE.test(line)) continue;
    /* Etiketten SONRAKİ sayı alınır: "Toplam 1.234,56 TL" */
    const after = line.slice(line.search(LABEL));
    const m = after.match(/[-+]?[\d][\d.,\s']*\d|[-+]?\d/g);
    if (!m || !m.length) continue;
    const v = parseValue(m[m.length - 1]);
    if (v !== null && Number.isFinite(v) && Math.abs(v) > 0) found.push(Math.abs(v));
  }
  if (!found.length) return null;
  /* Birden çok aday varsa en büyüğü genel toplamdır (KDV dahil olan). */
  return Math.max(...found);
}
